import { useState } from "react";
import { useCompanyViewModel } from "../../viewmodels/company/useCompanyViewModel";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import GoogleMapPickerScript from "../../components/common/GoogleMapPickerScript";

const CreateCompany = () => {
  const navigate = useNavigate();

  const { createCompany, companyTypes, setTypeSearch, menus } =
    useCompanyViewModel();

  const [form, setForm] = useState<any>({
    name: "",
    description: "",
    contactEmail: "",
    contactPhone: "",
    address: "",
    websiteUrl: "",
    logo: "",
    longitude: "",
    latitude: "",
    companyTypeIds: [],
    isPublic: false,    
    menuIds: [],         
  });

  const updateForm = (key: string, value: any) => {
    setForm((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleType = (id: number) => {
    setForm((prev: any) => ({
      ...prev,
      companyTypeIds: prev.companyTypeIds.includes(id)
        ? prev.companyTypeIds.filter((x: number) => x !== id)
        : [...prev.companyTypeIds, id],
    }));
  };

  //  MENU TOGGLE
  const toggleMenu = (id: string) => {
    setForm((prev: any) => ({
      ...prev,
      menuIds: prev.menuIds.includes(id)
        ? prev.menuIds.filter((x: string) => x !== id)
        : [...prev.menuIds, id],
    }));
  };

  const handleMapSelect = (pos: any) => {
    setForm((prev: any) => ({
      ...prev,
      latitude: pos.lat.toString(),
      longitude: pos.lng.toString(),
    }));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const name = form.name?.trim();
    if (!name) return toast.error("Company name required");

    try {
      await createCompany({
        ...form,
        name,
      });

      window.location.href = "/companies";
    } catch (err: any) {
      toast.error(err?.response?.data?.message);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto">

        <div className="text-sm text-gray-500 mb-3">
          Dashboard / Companies / Create
        </div>

        <h2 className="text-xl font-semibold mb-4">
          Create Company
        </h2>

        <form
          onSubmit={handleSubmit}
          className="bg-white p-6 rounded shadow space-y-6"
        >

          {/* BASIC */}
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Company Name *"
              onChange={(e:any)=>updateForm("name", e.target.value)} />

            <Input label="Email"
              onChange={(e:any)=>updateForm("contactEmail", e.target.value)} />

            <Input label="Phone"
              onChange={(e:any)=>updateForm("contactPhone", e.target.value)} />

            <Input label="Website"
              onChange={(e:any)=>updateForm("websiteUrl", e.target.value)} />

            <Input label="Logo URL"
              onChange={(e:any)=>updateForm("logo", e.target.value)} />
          </div>

          <Textarea label="Description"
            onChange={(e:any)=>updateForm("description", e.target.value)} />

          <Textarea label="Address"
            onChange={(e:any)=>updateForm("address", e.target.value)} />

          {/* MAP */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium mb-2">
              Location
            </label>

            <GoogleMapPickerScript onSelect={handleMapSelect} />

            <div className="grid grid-cols-2 gap-4 mt-3">
              <input value={form.latitude} readOnly className="input bg-gray-100"/>
              <input value={form.longitude} readOnly className="input bg-gray-100"/>
            </div>
          </div>

          {/* TYPES */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium mb-2">
              Company Types
            </label>

            <input
              placeholder="Search..."
              className="w-full border px-3 py-2 rounded mb-2"
              onChange={(e)=>setTypeSearch(e.target.value)}
            />

            <div className="border rounded max-h-40 overflow-y-auto p-2">
              {companyTypes.map((t:any)=>(
                <label key={t.id} className="flex gap-2 mb-1">
                  <input
                    type="checkbox"
                    checked={form.companyTypeIds.includes(t.id)}
                    onChange={()=>toggleType(t.id)}
                  />
                  {t.name}
                </label>
              ))}
            </div>
          </div>

          {/*  PUBLIC */}
          <div className="border-t pt-4">
            <label className="flex gap-2 items-center">
              <input
                type="checkbox"
                checked={form.isPublic}
                onChange={(e)=>updateForm("isPublic", e.target.checked)}
              />
              Public Company
            </label>
          </div>

          {/* ✅ MENUS */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium mb-2">
              Menu Access
            </label>

            <div className="border rounded max-h-60 overflow-y-auto p-2">
              {menus.map((m:any)=>(
                <div key={m.id} className="mb-2">

                  {/* PARENT */}
                  <label className="flex gap-2 font-medium">
                    <input
                      type="checkbox"
                      checked={form.menuIds.includes(m.id)}
                      onChange={()=>toggleMenu(m.id)}
                    />
                    {m.name}
                  </label>

                  {/* CHILDREN */}
                  {m.children?.map((c:any)=>(
                    <label key={c.id} className="flex gap-2 ml-5 text-sm">
                      <input
                        type="checkbox"
                        checked={form.menuIds.includes(c.id)}
                        onChange={()=>toggleMenu(c.id)}
                      />
                      {c.name}
                    </label>
                  ))}

                </div>
              ))}
            </div>
          </div>

          {/* ACTION */}
          <div className="flex justify-end gap-3 border-t pt-4">
            <button type="button" onClick={()=>navigate("/companies")} className="px-4 py-2 border rounded-lg">
              Cancel
            </button>

            <button className="px-5 py-2 bg-blue-600 text-white rounded-lg">
              Save Company
            </button>
          </div>

        </form>
      </div>
    </DashboardLayout>
  );
};

const Input = ({ label, ...props }: any) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input {...props} className="w-full border px-3 py-2 rounded"/>
  </div>
);

const Textarea = ({ label, ...props }: any) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <textarea rows={3} {...props} className="w-full border px-3 py-2 rounded"/>
  </div>
);

export default CreateCompany;